import {Buffer} from 'buffer';
import {clamp} from 'lodash';
import {
  KeyReference,
  LedApi3LightDto,
  LedApi3LightDtoUiInner,
  UiType,
} from 'puffco-api-axios-client';

import {LED3Data} from '../../../led3/led3data';
import {
  MoodType,
  ParamPrimitiveValue,
  Params,
  Projector,
  isSliderUI,
} from '../../../types';
import {convertHexStringToNumArray} from '../../../utils/convertHexStringToNumArray';
import {math} from '../../../utils/math';
import {EvaluatedProjector} from './EvaluatedProjector';

type NormalizedContext = Record<string, ParamPrimitiveValue>;
type Formatter = (value: ParamPrimitiveValue) => Buffer;

const isKeyReference = (k: unknown): k is KeyReference =>
  !!(k as KeyReference)._key;

export type Context = Record<string, ParamPrimitiveValue>;

const userColorsToArray = (userColors: string | string[]) => {
  let colors: string[] = [];

  if (Array.isArray(userColors)) {
    colors = userColors;
  }

  if (typeof userColors === 'string') {
    if (userColors.includes(',')) {
      colors = userColors.split(',');
    } else {
      colors = [userColors];
    }
  }

  // make sure there are no #s
  colors = colors.map(color => color.replace('#', ''));

  return colors;
};

export class MoodLightEvaluator {
  private static readonly formatters: Record<string, Formatter> = {
    rgbArray: value => {
      if (!Array.isArray(value))
        throw new Error(
          `Input value ${value} for rgbArray formatting is not an array`,
        );

      return Buffer.from(convertHexStringToNumArray(value.join('')));
    },
  };

  /**
   *
   * @param moodlight
   * @param initialContext
   * @returns the evaluated mood light with resolved param
   */
  public static evaluate(
    moodLight: LedApi3LightDto,
    initialContext: Context = {},
  ) {
    const evaluation = MoodLightEvaluator.buildEvaluationContext(
      {
        ui: moodLight.ui,
        projector: moodLight.projector,
      },
      initialContext,
    );

    const context = {
      ...evaluation,
      ...(evaluation.userColors && {
        userColors: userColorsToArray(evaluation.userColors as string[]),
      }),
    };

    Object.entries(moodLight.mappings).forEach(([key, operation]) => {
      math.evaluate(`${key} = ${operation}`, context);
    });

    const normalizedContext = Object.entries(context).reduce<NormalizedContext>(
      (obj, [key, value]) => {
        obj[key] = value.valueOf();

        return obj;
      },
      {},
    );

    const param = MoodLightEvaluator.resolveMappings(
      moodLight.projector,
      normalizedContext,
    );

    const {variant, ...meta} = evaluation;

    return new EvaluatedProjector({
      lamp: {
        name: moodLight.projector.lamp.name,
        param,
      },
      meta: {
        led3Name: moodLight.name,
        ...(moodLight.tag && {led3Tag: moodLight.tag}),
        ...meta,
      },
    });
  }

  /**
   * Builds the execution context for the mood light evaluation
   * The execution context is an object having UI compenent keys as keys and their coresponding values taken from projector's `meta` property as values
   * If `meta` values ar missing it falls back to `default` value of the UI component
   *
   * Color picker value must be parsed from an array represented as a string to a string array otherwise evaluation will fail
   *
   * @param param0 moodlight's ui and projector
   * @returns execution context object
   *
   * @example
   *  const result = MoodLightEvaluationContext.evaluate({
   *     ui: [{type: 'colors', key: 'userColors', default: '#FF0000,#00FFFF'}],
   *     projector: {
   *       meta: {userColors: ['#ABCDEF', '#FEDCBA']},
   *     },
   *   });
   *
   *  console.log(result) //  { userColors: ['#ABCDEF', '#FEDCBA'] }
   */
  public static buildEvaluationContext(
    {ui, projector}: Pick<LedApi3LightDto, 'ui' | 'projector'>,
    context: Context,
  ) {
    const values = ui.reduce<Context>((obj, current) => {
      const key = current.key;

      const value =
        projector.meta[key] ??
        context[key] ??
        (current.type === UiType.Colors
          ? userColorsToArray(current.default as string).map(
              color => `#${color}`,
            )
          : current.default);

      obj[key] = MoodLightEvaluator.normalizeValue(value, current);

      return obj;
    }, {});

    return {...context, ...values};
  }

  /**
   * Resolves projector param mappings from context while formatting necessary references
   * @param projector
   * @param context
   * @returns resolved params
   */
  private static resolveMappings(
    projector: Projector,
    context: NormalizedContext,
  ) {
    return Object.entries(projector.lamp.param).reduce<Params<never>>(
      (obj, [key, value]) => {
        if (!isKeyReference(value)) {
          obj[key] = value;

          return obj;
        }

        const format = value._format;
        const shouldFormat = !!(
          format && MoodLightEvaluator.formatters[format]
        );

        obj[key] = shouldFormat
          ? MoodLightEvaluator.formatters[format](context[value._key])
          : context[value._key];

        return obj;
      },
      {},
    );
  }

  private static normalizeValue(value: any, ui: LedApi3LightDtoUiInner) {
    if (isSliderUI(ui)) return clamp(value, ui.min, ui.max);

    return value;
  }
}

export const fallback = MoodLightEvaluator.evaluate(
  LED3Data[MoodType.NO_ANIMATION],
  {variant: 1, userColors: ['#FFFFFF']},
).value;
